/**
 * Centralized stale time tiers for React Query.
 * Controls how long data is considered "fresh" before background revalidation.
 */
export const staleTimes = {
  /** Data that rarely changes within a session (user profile, groups, general settings) */
  identity: 1000 * 60 * 60, // 60 minutes

  /** Data that changes infrequently via admin operations (projects, users, vendors, cost codes, tags) */
  masterData: 1000 * 60 * 40, // 40 minutes

  /** Data that changes moderately (billing periods, vendor details, cost code balances) */
  referenceData: 1000 * 60 * 10, // 10 minutes

  /** Data fetched with specific context (invoice detail, PO detail, change order detail) */
  detail: 1000 * 60 * 5, // 5 minutes

  /** Data that changes frequently - core operational screens (invoice lists, inbox lists) */
  transactional: 1000 * 60 * 2, // 2 minutes

  /** Near-real-time data (inbox counts, latest IDs) */
  realtime: 1000 * 30 // 30 seconds
} as const;

/**
 * Centralized query keys for React Query.
 * Prevents typos and enables type-safe invalidation.
 */
export const queryKeys = {
  // Auth / User
  me: ['me'] as const,
  groups: ['groups'] as const,
  generalSettings: ['general-settings'] as const,

  // Master data
  vendors: ['vendors'] as const,
  vendorsSearch: (params: unknown) => ['vendors', params] as const,
  vendor: (id: number | null) => ['vendor', id] as const,
  vendorBank: (vendorId: number) => ['vendor-bank', vendorId] as const,
  vendorAddress: (vendorId: number) => ['vendor-address', vendorId] as const,
  costCodesWithParent: ['cost-codes-with-parent'] as const,
  costCodesWithoutParent: ['cost-codes-without-parent'] as const,
  projectsActive: ['projects-only-active'] as const,
  projectsActiveParent: ['projects-only-active-parent'] as const,
  projectsAll: ['projects-all'] as const,
  project: (id: number | null) => ['project', id] as const,
  projectEdit: (id: number) => ['project-edit', id] as const,
  projectOriginalBudgetTotal: (id: number) => ['project-original-budget-total', id] as const,
  projectBudgetFigures: (id: number) => ['project-budget-figures', id] as const,
  usersActive: ['users-active'] as const,
  users: ['users'] as const,
  tags: ['tags'] as const,

  // Transactional data
  invoicesRoot: ['invoices'] as const,
  invoices: (params: unknown) => ['invoices', params] as const,
  inboxReceived: (...args: unknown[]) => ['inbox-received-data', ...args] as const,
  latestInvoiceIds: (params: unknown) => ['latest-invoice-ids', params] as const,
  latestReviewIds: (params: unknown) => ['latest-review-ids', params] as const,
  review: (id: number) => ['review', id] as const,
  notifications: ['notifications'] as const,
  notificationsUnreadCount: ['notifications-unread-count'] as const,

  // Approvals
  approvals: (status: string | string[]) => [`approvals-${status}-data`] as const,
  alternativeCompanyReviews: ['approvals-alternative-companies-data'] as const,
  alternativeCompanyReviewsCount: ['approvals-alternative-companies-count'] as const,

  // Project-scoped data
  billingPeriods: (projectId: number) => ['billing-periods-by-project', projectId] as const,
  costCodeBalance: (projectId: number) => ['cost-code-balance', projectId] as const,
  projectTemplateSettingsAll: ['project-template-settings'] as const,
  projectTemplateSettings: (projectId: number, documentType: string) =>
    ['project-template-settings', projectId, documentType] as const,

  // Template library
  documentTypes: ['document-types'] as const,
  documentOverview: ['document-overview'] as const,
  projectDocumentsAll: ['project-documents'] as const,
  projectDocuments: (projectId: number) => ['project-documents', projectId] as const,
  documentPreviewTarget: (templateId: number) => ['document-preview-target', templateId] as const,
  templateSuggestions: (versionId: number) => ['template-suggestions', versionId] as const,
  payAppDocuments: (billingPeriodId: number) => ['pay-app-documents', billingPeriodId] as const,
  documentTemplatesAll: ['document-templates'] as const,
  documentTemplates: (documentType: string) => ['document-templates', documentType] as const,
  documentTemplateVersions: (templateId: number) => ['document-template-versions', templateId] as const,
  documentTemplateFields: (documentType: string) => ['document-template-fields', documentType] as const,
  documentTemplateProjectsAll: ['document-template-projects'] as const,
  documentTemplateProjects: (documentType: string) => ['document-template-projects', documentType] as const,
  documentTemplateSheet: (versionId: number, sheet: string | null) =>
    ['document-template-sheet', versionId, sheet] as const
} as const;

/**
 * Query keys that should be persisted to localStorage.
 * Only master/identity data - NOT transactional data.
 */
export const persistableQueryKeys = [
  'me',
  'groups',
  'general-settings',
  'vendors',
  'vendor',
  'cost-codes-with-parent',
  'cost-codes-without-parent',
  'projects-only-active',
  'projects-only-active-parent',
  'projects-all',
  'project',
  'users-active',
  'users',
  'tags',
  'billing-periods-by-project',
  'inbox-received-data',
  'approvals-pending-data',
  'approvals-all-data',
  'approvals-disputed-data',
  'approvals-approved-data'
] as const;
